import React from "react";
import type { SingleValue } from "react-select";
import t from "../../../../../i18n";
import type { BaseOption } from "@ui/ReactSelect/useFormatOptionLabel";
import { emptyOption } from "@ui/ReactSelect/useFormatOptionLabel";
import type { ApiValue } from "../../Forms/formHelpers";
import { getDefaultValue, multipleValue } from "../../Forms/formHelpers";
import { uniq } from "diagnostics-lib/array";
import {
  useCategories,
  useCreateCategory,
  useDeleteCategory,
  useUpdateCategory,
} from "../../../../../api/useCategories";
import type { ClassificationFailure } from "../../../../../api/useClassificationFailures";

// Category (unique with full option)
export const useCategoryInput = (selectedFailures: ClassificationFailure[]) => {
  const [placeholder, setPlaceholder] = React.useState(
    t("administration.failures.table.header.category"),
  );
  const categories = useCategories("FAILURE_CATEGORY");
  const createCategory = useCreateCategory("FAILURE_CATEGORY");
  const updateCategory = useUpdateCategory("FAILURE_CATEGORY");
  const deleteCategory = useDeleteCategory("FAILURE_CATEGORY");
  const [isDirty, setIsDirty] = React.useState(false);

  const categoryIds = uniq(
    selectedFailures
      .map(f => f.failureCategoryId)
      .map(failureCategoryId => ({
        id: failureCategoryId ?? "",
        name: failureCategoryId ?? "",
      })),
  );
  const defaultValue = getDefaultValue<ApiValue>(categoryIds, "id", category => ({
    label: category.name,
    value: category.id,
  }));

  const categoryOptions = [
    { id: emptyOption.value, name: emptyOption.label },
    ...categories.map(c => ({ id: c, name: c })),
  ];

  const [defaultCategoryOption, setDefaultCategoryOption] = React.useState(() => {
    if (defaultValue?.label === "" || defaultValue?.label === multipleValue.label) return null;
    return defaultValue;
  });

  const [selectedCategory, setSelectedCategory] = React.useState<SingleValue<BaseOption>>(
    defaultCategoryOption?.label === "" ? null : defaultCategoryOption,
  );

  const handleCategoryChanged = React.useCallback((option: SingleValue<BaseOption>) => {
    setSelectedCategory(option);
  }, []);

  React.useEffect(() => {
    if (selectedCategory?.value === defaultCategoryOption?.value) {
      setIsDirty(false);
    } else {
      setIsDirty(true);
    }
  }, [selectedCategory, defaultCategoryOption]);

  const resetValue = React.useCallback(() => {
    setSelectedCategory(defaultCategoryOption?.label === "" ? null : defaultCategoryOption);
    setIsDirty(false);
  }, [defaultCategoryOption]);

  const handleCategoryCreated = React.useCallback(
    async (newCategory: string) => {
      await createCategory(newCategory);
      setIsDirty(true);

      setSelectedCategory({
        label: newCategory,
        value: newCategory,
      });
    },
    [createCategory],
  );

  const handleCategoryEdited = React.useCallback(
    async (option: BaseOption, label: string) => {
      if (!option.value) return;
      await updateCategory(option.value, label);
      if (selectedCategory?.value === option.value) {
        setSelectedCategory({ ...option, label });
      }
    },
    [updateCategory, selectedCategory, setSelectedCategory],
  );

  const handleCategoryDeleted = React.useCallback(
    async (option: BaseOption) => {
      if (!option.value) return;
      await deleteCategory(option.value);
      if (selectedCategory?.value === option.value) {
        setDefaultCategoryOption(null);
        setSelectedCategory(null);
      }
    },
    [deleteCategory, selectedCategory],
  );

  React.useEffect(() => {
    if (defaultValue?.value === multipleValue.value)
      setPlaceholder(t("administration.modal.multiple_value"));
    else setPlaceholder(t("form.no_value"));
  }, [defaultValue]);

  return {
    placeholder,
    categoryOptions,
    selectedCategory,
    setSelectedCategory,
    handleCategoryChanged,
    handleCategoryCreated,
    handleCategoryEdited,
    handleCategoryDeleted,
    isDirty,
    resetValue,
  };
};
